import { OpusAgentChatTurnStatus } from "@src/types.generated";
import { OpusAgentEventLike, getEventKind, toolRowLabel } from "../events";

// Pure helpers for the voice-mode call: the OpenAI Realtime wire vocabulary
// (pinned here so an API version bump touches one file) and the translation
// between Opus Agent turns and what the voice model is told.

const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";

// The SDP exchange must name the model even though the minted session does.
export const realtimeCallsUrl = (model: string): string =>
  `${REALTIME_CALLS_URL}?model=${encodeURIComponent(model)}`;
export const ASK_OPUS_AGENT_TOOL = "ask_opus_agent";
export const STOP_OPUS_AGENT_TOOL = "stop_opus_agent";

// The spoken layer only needs the start of the answer (the agent opens with a
// spoken summary); the rest stays on screen.
const ANSWER_MAX_CHARS = 2000;

export type RealtimeServerEvent =
  | { type: "input_audio_buffer.speech_started" }
  | { type: "input_audio_buffer.speech_stopped" }
  | { type: "output_audio_buffer.started" }
  | { type: "output_audio_buffer.stopped" }
  | { type: "output_audio_buffer.cleared" }
  | {
      type: "response.function_call_arguments.done";
      call_id: string;
      name: string;
      arguments: string;
    }
  | {
      type: "conversation.item.input_audio_transcription.completed";
      transcript: string;
    }
  | { type: "conversation.item.input_audio_transcription.failed" }
  | { type: "response.output_audio_transcript.done"; transcript: string }
  | {
      type: "response.created";
      response: { id: string; metadata?: Record<string, unknown> | null };
    }
  | {
      type: "response.done";
      response: {
        id: string;
        status?: string;
        output?: { type?: string }[] | null;
        usage?: RealtimeUsage | null;
      };
    }
  | {
      type: "error";
      error: { message?: string; code?: string; event_id?: string };
    }
  | { type: "__other__" };

export type RealtimeUsage = {
  input_token_details?: { audio_tokens?: number; text_tokens?: number };
  output_token_details?: { audio_tokens?: number; text_tokens?: number };
};

export type VoiceUsageTotals = {
  inputAudioTokens: number;
  outputAudioTokens: number;
  inputTextTokens: number;
  outputTextTokens: number;
};

export const EMPTY_USAGE: VoiceUsageTotals = {
  inputAudioTokens: 0,
  outputAudioTokens: 0,
  inputTextTokens: 0,
  outputTextTokens: 0,
};

export const addUsage = (
  totals: VoiceUsageTotals,
  usage: RealtimeUsage | null | undefined,
): VoiceUsageTotals => ({
  inputAudioTokens:
    totals.inputAudioTokens + (usage?.input_token_details?.audio_tokens ?? 0),
  outputAudioTokens:
    totals.outputAudioTokens + (usage?.output_token_details?.audio_tokens ?? 0),
  inputTextTokens:
    totals.inputTextTokens + (usage?.input_token_details?.text_tokens ?? 0),
  outputTextTokens:
    totals.outputTextTokens + (usage?.output_token_details?.text_tokens ?? 0),
});

export const parseServerEvent = (raw: string): RealtimeServerEvent => {
  try {
    const event = JSON.parse(raw);
    return typeof event?.type === "string" ? event : { type: "__other__" };
  } catch {
    return { type: "__other__" };
  }
};

export const parseAskRequest = (argumentsJson: string): string | null => {
  try {
    const parsed = JSON.parse(argumentsJson);
    const request =
      typeof parsed?.request === "string" ? parsed.request.trim() : "";
    return request || null;
  } catch {
    return null;
  }
};

// Client events we send over the data channel.

export type ClientEvent = { type: string } & Record<string, unknown>;

export const functionCallOutput = (
  callId: string,
  output: unknown,
): ClientEvent => ({
  type: "conversation.item.create",
  item: {
    type: "function_call_output",
    call_id: callId,
    output: JSON.stringify(output),
  },
});

export const createResponse = (
  options: { noTools?: boolean } = {},
): ClientEvent => ({
  type: "response.create",
  ...(options.noTools ? { response: { tool_choice: "none" } } : {}),
});

// A spoken progress line outside the conversation: no context to answer
// from (a progress reply that could see the question once made up "I can't
// see that" while Opus Agent was still working), and nothing left behind in
// the conversation for later turns to build on.
export const progressResponse = (label: string): ClientEvent => ({
  type: "response.create",
  response: {
    conversation: "none",
    input: [],
    tool_choice: "none",
    instructions: `Say only this, word for word, then stop: "Still working on it: ${label.slice(0, 80)}."`,
  },
});

// The model is fixed by the call itself; session.update may not change it.
export const sessionUpdate = (config: unknown): ClientEvent => {
  const session: Record<string, unknown> =
    config && typeof config === "object"
      ? { ...(config as Record<string, unknown>) }
      : {};
  delete session.model;
  return { type: "session.update", session: { ...session, type: "realtime" } };
};

export const systemNote = (text: string): ClientEvent => ({
  type: "conversation.item.create",
  item: {
    type: "message",
    role: "system",
    content: [{ type: "input_text", text }],
  },
});

// Turn → what the voice model hears.

type TurnLike = {
  id: string;
  userMessage: string;
  status: OpusAgentChatTurnStatus;
  supersededByEditAt?: string | null;
  events: OpusAgentEventLike[];
  sendFailed?: boolean;
  pollFailed?: boolean;
  pending?: boolean;
};

// The voice turn a pending ask_opus_agent call is waiting on: the newest live
// turn carrying exactly that request that did not exist when the call sent it
// (so asking the same question twice never answers from the earlier turn).
export const findAskTurn = <T extends TurnLike>(
  turns: T[],
  request: string,
  preexistingIds: ReadonlySet<string>,
): T | undefined =>
  [...turns]
    .reverse()
    .find(
      (turn) =>
        !preexistingIds.has(turn.id) &&
        turn.userMessage === request &&
        !turn.supersededByEditAt,
    );

// The answer the author sees last: the assistant text after the final
// activity row (earlier text is narration between tool calls).
export const finalAnswerText = (events: OpusAgentEventLike[]): string => {
  const sorted = [...events].sort((a, b) => a.index - b.index);
  let lastActivity = -1;
  sorted.forEach((event, position) => {
    const kind = getEventKind(event.payload);
    if (kind === "toolCall" || kind === "planUpdate") lastActivity = position;
  });
  const texts = (from: number) =>
    sorted
      .slice(from)
      .map((event) => event.payload.assistantText?.text ?? "")
      .filter(Boolean)
      .join("\n\n")
      .trim();
  return texts(lastActivity + 1) || texts(0);
};

const letter = (index: number) => String.fromCharCode(65 + index);
const mentions = (text: string, phrase: string) =>
  text.toLowerCase().includes(phrase.trim().toLowerCase());

// The turn's latest event carrying a field, if any.
const latestPayload = <K extends "questions" | "planProposal">(
  events: OpusAgentEventLike[],
  key: K,
) =>
  [...events]
    .sort((a, b) => a.index - b.index)
    .reverse()
    .find((event) => event.payload[key])?.payload[key];

/**
 * A spoken rendering of the cards a turn ended on (questions, a plan), which
 * otherwise only show on screen. Left out when the assistant text already
 * says it: every question's text, or the plan's title.
 */
export const spokenCardsText = (
  events: OpusAgentEventLike[],
  answer: string,
): string => {
  const parts: string[] = [];
  const questions = latestPayload(events, "questions")?.questions ?? [];
  if (
    questions.length &&
    !questions.every((question) => mentions(answer, question.text))
  )
    questions.forEach((question, position) => {
      const options = question.options
        .map((option, index) => `${letter(index)}, ${option}`)
        .join("; ");
      parts.push(
        `Question ${position + 1} of ${questions.length}: ${question.text.trim()} ` +
          `Options: ${options}.${question.multiSelect ? " Pick any." : ""}`,
      );
    });
  const plan = latestPayload(events, "planProposal");
  if (plan && !mentions(answer, plan.title)) {
    const count = plan.items.length;
    parts.push(
      `I've put a plan on screen: ${plan.title.trim()}, ${count} ${count === 1 ? "item" : "items"}. ` +
        "Tap Approve & build on screen to build it, or tell me what to change.",
    );
  }
  return parts.join("\n");
};

export type AskOutcome =
  | { status: "succeeded"; answer: string }
  | { status: "failed"; error: string }
  | { status: "stopped" };

// Null while the turn is still working.
export const askOutcome = (turn: TurnLike | undefined): AskOutcome | null => {
  if (!turn || turn.pending) return null;
  if (turn.sendFailed)
    return { status: "failed", error: "The request could not be sent." };
  if (turn.pollFailed && turn.status === OpusAgentChatTurnStatus.Running)
    return {
      status: "failed",
      error: "Lost touch with Opus Agent. Its answer will appear on screen.",
    };
  switch (turn.status) {
    case OpusAgentChatTurnStatus.Succeeded: {
      const text = finalAnswerText(turn.events);
      // The cards go after the capped text, so a long answer never cuts them.
      const cards = spokenCardsText(turn.events, text);
      const capped =
        text.length > ANSWER_MAX_CHARS
          ? `${text.slice(0, ANSWER_MAX_CHARS)}…`
          : text;
      return {
        status: "succeeded",
        answer:
          [capped, cards].filter(Boolean).join("\n\n") ||
          "Done. The details are on screen.",
      };
    }
    case OpusAgentChatTurnStatus.Failed: {
      const error = turn.events.find((event) => event.payload.error)?.payload
        .error?.message;
      return { status: "failed", error: error ?? "Opus Agent hit an error." };
    }
    case OpusAgentChatTurnStatus.Aborted:
      return { status: "stopped" };
    default:
      return null;
  }
};

// The newest tool activity label, for a spoken progress note.
export const latestActivityLabel = (
  events: OpusAgentEventLike[],
): string | null => {
  const tools = events
    .filter((event) => event.payload.toolCall)
    .sort((a, b) => a.index - b.index);
  const last = tools[tools.length - 1]?.payload.toolCall;
  if (!last) return null;
  return last.label || toolRowLabel(last.toolName, last.detail);
};

// Realtime runs one response at a time and rejects a response.create while
// another is in flight (an acknowledgement, the greeting, a server-started
// reply to speech); a rejected create would drop the spoken answer. The gate
// holds the newest request until the in-flight responses finish. Our creates
// carry an event_id (echoed on errors) and the same id in response.metadata
// (echoed on response.created), so a collision retries exactly the request
// that collided, never an old one.
export class ResponseGate {
  private readonly active = new Set<string>();
  private awaiting: ClientEvent | null = null;
  private queued: ClientEvent | null = null;
  private seq = 0;

  constructor(private readonly send: (event: ClientEvent) => void) {}

  get busy(): boolean {
    return this.active.size > 0 || this.awaiting !== null;
  }

  request(event: ClientEvent): void {
    if (this.busy) {
      this.queued = event;
      return;
    }
    const eventId = `opus_${++this.seq}`;
    const response = (event.response ?? {}) as Record<string, unknown>;
    const tagged = {
      ...event,
      event_id: eventId,
      response: { ...response, metadata: { client_event_id: eventId } },
    };
    this.awaiting = tagged;
    this.send(tagged);
  }

  // Sends only when nothing is in flight; a dropped one is not retried.
  sendIfIdle(event: ClientEvent): boolean {
    if (this.busy) return false;
    this.send(event);
    return true;
  }

  created(responseId: string, metadata?: Record<string, unknown> | null): void {
    this.active.add(responseId);
    if (this.awaiting && metadata?.client_event_id === this.awaiting.event_id)
      this.awaiting = null;
  }

  done(responseId: string): void {
    this.active.delete(responseId);
    this.drain();
  }

  error(eventId: string | undefined, code: string | undefined): void {
    if (!this.awaiting || eventId !== this.awaiting.event_id) return;
    const collided = this.awaiting;
    this.awaiting = null;
    if (code === "conversation_already_has_active_response")
      this.queued ??= collided;
    this.drain();
  }

  reset(): void {
    this.active.clear();
    this.awaiting = null;
    this.queued = null;
  }

  private drain(): void {
    if (this.busy || !this.queued) return;
    const next = this.queued;
    this.queued = null;
    // request() re-tags it with a fresh event_id.
    this.request(next);
  }
}
